import { displayDateUS } from "../../utils/dateUtils";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

/**
 * Matches parsing logic in IdentificationCard.jsx
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseIdentificationCardFromApiResponse(
  response,
  targetUserId,
) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.card) {
    return result.card;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null ||
      result.employeeName != null ||
      result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

function formatFeeAmount(raw) {
  const n =
    typeof raw === "number" && !Number.isNaN(raw)
      ? raw
      : parseFloat(String(raw ?? ""));
  const v = Number.isFinite(n) ? n : 15;
  return v.toFixed(2);
}

/**
 * Aligned with IdentificationCard.jsx copy and fields.
 * @param {Record<string, unknown>} record
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildIdentificationCardPrintHtml(record, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const employeeName = escapeHtml(
    String(record.employeeName || record.applicantName || "").trim(),
  );
  const formDate = escapeHtml(formatDate(record.formDate));
  const position = escapeHtml(
    String(record.positionAppliedFor || record.position || ""),
  );
  const fee = formatFeeAmount(record.replacementFeeAmount);
  const feeLabel = escapeHtml(fee);

  const es = record.employeeSignature;
  const nestedEmpSig =
    es && typeof es === "object"
      ? /** @type {{ signature?: string }} */ (es).signature
      : null;
  const applicantSig =
    record.applicantSignature ||
    record.empSignature ||
    nestedEmpSig;
  const applicantSignedAt = escapeHtml(
    formatDate(
      record.applicantSignedAt ||
        record.applicantSignatureDate ||
        record.employeeSignatureDate ||
        record.empSignedAt,
    ),
  );
  const applicantSignedBy = escapeHtml(
    String(record.applicantSignedBy || "").trim(),
  );

  const reviewedBySignedBy = escapeHtml(
    String(record.reviewedBySignedBy || "").trim(),
  );
  const reviewedBySig =
    record.reviewedBySignature || record.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      record.reviewedBySignedAt ||
        record.adminSignatureDate ||
        record.reviewedBySignatureDate,
    ),
  );

  const statementAccepted = !!record.applicantStatementAccepted;
  const statusCompleted =
    String(record.status || "").toLowerCase() === "completed";
  const showCertChecked = statementAccepted || statusCompleted;

  const displayName = employeeName || applicantSignedBy || "—";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Identification card — ${employeeName || "Employee"}</title>
  <style>
    @page { margin: 0.4in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 11px;
      line-height: 1.45;
      color: #111827;
      margin: 0;
      padding: 12px;
    }
    .sheet { max-width: 880px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body {
        padding: 28px 18px 40px;
        background: transparent;
        min-height: 100vh;
      }
      .sheet {
        background: #ffffff;
        padding: 32px 36px 40px;
        border: 1px solid #e2e8f0;
        border-radius: 1px;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.05),
          0 24px 48px -16px rgba(15, 23, 42, 0.22);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet {
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        background: #fff !important;
      }
    }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 16px;
    }
    .logo { height: 56px; width: auto; max-width: 200px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 16px; font-weight: 700; color: #111827; margin: 0 0 4px 0; }
    .title-sub { font-size: 13px; font-weight: 500; color: #2563eb; margin: 0; }
    .intro {
      margin-bottom: 18px;
      text-align: justify;
      font-size: 11px;
      color: #374151;
      line-height: 1.55;
    }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px 20px;
      margin-bottom: 18px;
    }
    .field-label {
      font-size: 10px;
      font-weight: 600;
      color: #6b7280;
      margin-bottom: 4px;
    }
    .field-value {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 8px 10px;
      min-height: 38px;
      background: #fff;
      font-size: 12px;
      color: #111827;
    }
    .cert-box {
      margin-top: 8px;
      padding: 12px 14px;
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
    }
    .cert-title { font-weight: 700; font-size: 11px; letter-spacing: 0.02em; margin: 0 0 4px 0; }
    .cert-sub { font-size: 9px; color: #6b7280; margin: 0 0 10px 0; text-transform: uppercase; }
    .cert-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 10px 12px;
      font-size: 11px;
    }
    .chk { width: 14px; height: 14px; margin-top: 2px; flex-shrink: 0; }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-top: 20px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fafafa;
    }
    .sig-card h3 {
      font-size: 12px;
      font-weight: 700;
      margin: 0 0 10px 0;
      color: #111827;
    }
    .sig-label {
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 0.06em;
      color: #6b7280;
      margin: 8px 0 4px 0;
    }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 28px;
      font-size: 12px;
      padding: 4px 0;
    }
    .sig-img { max-height: 48px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .footer-print {
      margin-top: 24px;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 10px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Identification card</p>
        <p class="title-sub">${company}</p>
      </div>
    </div>

    <p class="intro">
      This statement is to acknowledge receipt of our agency's
      Identification Card. Due to the cost in producing these cards,
      the agency will be imposing a $${feeLabel} replacement fee for lost
      cards.
    </p>

    <div class="grid">
      <div>
        <div class="field-label">Employee name</div>
        <div class="field-value">${employeeName || "—"}</div>
      </div>
      <div>
        <div class="field-label">Date</div>
        <div class="field-value">${formDate || "—"}</div>
      </div>
      <div>
        <div class="field-label">Position applied for</div>
        <div class="field-value">${position || "—"}</div>
      </div>
      <div>
        <div class="field-label">Replacement fee (lost card)</div>
        <div class="field-value">$${feeLabel}</div>
      </div>
    </div>

    <div class="cert-box">
      <p class="cert-title">Applicant certification</p>
      <p class="cert-sub">Do not sign until you have read the above applicant statement.</p>
      <div class="cert-row">
        <input type="checkbox" class="chk" ${showCertChecked ? "checked" : ""} disabled />
        <span>I certify that I have read, fully understand and accept all terms of the foregoing Applicant Statement. <span style="color:#dc2626">*</span></span>
      </div>
    </div>

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${displayName}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
